import DataObjectIcon from "@mui/icons-material/DataObject";
import ImageIcon from "@mui/icons-material/Image";
import ListAltIcon from "@mui/icons-material/ListAlt";
import MapIcon from "@mui/icons-material/Map";
import TimelineIcon from "@mui/icons-material/Timeline";
import { ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { constructPanelForMessagePath } from "@/shared/components/visualization/DndContainer/dropHandlers";
import {
  isViewableAsMap,
  isViewableAsPlot,
  isViewableAsImage,
  isViewableAsRawMessage,
  isViewableAsConsoleLog,
} from "@/shared/components/visualization/Panel/predicates";
import { type MessagePathNode } from "@/shared/domain/topics";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { PanelType, useVizDispatch } from "@/shared/state/visualization";

interface ContextMenuProps {
  node: MessagePathNode;
  anchorElement: HTMLElement | null;
  closeMenu: () => void;
}

interface MenuItemProps {
  key: string;
  onClick: () => void;
  icon: JSX.Element;
  text: string;
}

function createMenuItem({
  key,
  onClick,
  icon,
  text,
}: MenuItemProps): JSX.Element {
  return (
    <MenuItem key={key} onClick={onClick}>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText>
        <RobotoTypography
          sx={{
            fontSize: "0.85rem",
          }}
        >
          {text}
        </RobotoTypography>
      </ListItemText>
    </MenuItem>
  );
}

export function ContextMenu({
  node,
  anchorElement,
  closeMenu,
}: ContextMenuProps) {
  const dispatch = useVizDispatch();

  /**
   * Due to the use of the `isViewableAs...` predicates, this error callback _should_ never be called.
   */
  const onError = (err: unknown) => {
    ErrorMonitoringService.captureError(err);
  };

  const menuItems = [];
  if (isViewableAsImage(node)) {
    const handleImagePanelOption = () => {
      closeMenu();
      constructPanelForMessagePath(dispatch, node, PanelType.Image, onError);
    };

    menuItems.push(
      createMenuItem({
        key: "image",
        onClick: handleImagePanelOption,
        icon: <ImageIcon fontSize="small" />,
        text: "View in Image panel",
      }),
    );
  }

  if (isViewableAsConsoleLog(node)) {
    const handleLogPanelOption = () => {
      closeMenu();
      constructPanelForMessagePath(dispatch, node, PanelType.Log, onError);
    };

    menuItems.push(
      createMenuItem({
        key: "log",
        onClick: handleLogPanelOption,
        icon: <ListAltIcon fontSize="small" />,
        text: "View in Log panel",
      }),
    );
  }

  if (isViewableAsMap(node)) {
    const handleMapPanelOption = () => {
      closeMenu();
      constructPanelForMessagePath(dispatch, node, PanelType.Map, onError);
    };

    menuItems.push(
      createMenuItem({
        key: "map",
        onClick: handleMapPanelOption,
        icon: <MapIcon fontSize="small" />,
        text: "View in Map panel",
      }),
    );
  }

  if (isViewableAsPlot(node)) {
    const handlePlotPanelOption = () => {
      closeMenu();
      constructPanelForMessagePath(dispatch, node, PanelType.Plot, onError);
    };

    menuItems.push(
      createMenuItem({
        key: "plot",
        onClick: handlePlotPanelOption,
        icon: <TimelineIcon fontSize="small" />,
        text: "View in Plot panel",
      }),
    );
  }

  if (isViewableAsRawMessage(node)) {
    const handleRawMessagePanelOption = () => {
      closeMenu();
      constructPanelForMessagePath(
        dispatch,
        node,
        PanelType.RawMessage,
        onError,
      );
    };

    menuItems.push(
      createMenuItem({
        key: "raw",
        onClick: handleRawMessagePanelOption,
        icon: <DataObjectIcon fontSize="small" />,
        text: "View in Raw panel",
      }),
    );
  }

  return (
    <Menu
      id="context-menu"
      anchorEl={anchorElement}
      open={menuItems.length > 0 && anchorElement !== null}
      onClose={closeMenu}
    >
      {menuItems}
    </Menu>
  );
}
